import { ShareLinkFile } from "../entities";
import { basenameFromPath } from "./fileFormat";
import { fileIconAsset } from "./fileIcon";

/** The list-view columns a user can sort by (one per clickable header). */
export type FileSortKey = "type" | "name" | "size" | "modified";

export type SortDirection = "asc" | "desc";

export type FileSort = {
  key: FileSortKey;
  direction: SortDirection;
};

/**
 * The published space's starting order: by type, ascending; folders (their own
 * kind, ranked first) followed by the files grouped by their type icon. A
 * listing that starts unsorted (the share-link viewer) keeps the order it was
 * delivered in until a header is clicked.
 */
export const DEFAULT_FILE_SORT: FileSort = { key: "type", direction: "asc" };

/**
 * The direction a column starts in on its first click. Names read A→Z, while
 * size and last-modified are nearly always asked for biggest / newest first,
 * which is also where Finder, Explorer, Drive and Dropbox start them.
 */
const INITIAL_DIRECTION: Record<FileSortKey, SortDirection> = {
  type: "asc",
  name: "asc",
  size: "desc",
  modified: "desc",
};

/**
 * Reducer for a header click: clicking the active column flips its direction;
 * clicking any other column (or the first click on an unsorted listing)
 * switches to it, in that column's initial direction.
 */
export function nextSort(current: FileSort | null, key: FileSortKey): FileSort {
  if (current?.key === key) {
    return {
      key,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }
  return { key, direction: INITIAL_DIRECTION[key] };
}

/**
 * A directory-listing entry (folder or file) reduced to its sortable fields,
 * so folders and files order as one mixed sequence. Build via
 * {@link fileSortEntry} / {@link folderSortEntry}.
 */
export type SortableEntry = {
  /**
   * Stable, unique identity of the entry (a file's uid, a folder's key). Has
   * the final say when everything else compares equal, so the order is total
   * (see {@link compareEntriesAsc}).
   */
  id: string;
  /**
   * Folders count as their own kind under the type sort: ahead of every file
   * ascending (so the default sort starts with the folders), after them
   * descending. Every other sort key mixes folders and files.
   */
  isFolder: boolean;
  name: string;
  size: number;
  /** Epoch millis of the last modification; 0 when unknown. */
  modifiedTime: number;
  /** Rank of the file's type under the type sort (see {@link typeRank}). */
  typeRank: number;
};

/**
 * The order the type sort puts file types in, by the icon the type column
 * displays for them: documents first, then plain text and data, then media,
 * archives, and anything we have no icon for last.
 *
 * Keyed by icon asset, so the grouping is exactly the one the user can see: a
 * `.doc` and a `.docx` render the same icon and therefore sort together, which
 * ordering by the raw MIME string ("application/msword" against
 * "application/vnd.openxmlformats-…") does not do.
 */
const TYPE_ORDER = [
  "pdf.svg",
  "word.svg",
  "excel.svg",
  "csv.svg",
  "ppt.svg",
  "text.svg",
  "image.svg",
  "video.svg",
  "audio.svg",
  "zip.svg",
  "unknown.svg",
];

/** Sort rank of a file's type; an unlisted icon sorts last, next to unknown. */
const typeRank = (mimeType?: string, fileName?: string): number => {
  const rank = TYPE_ORDER.indexOf(fileIconAsset(mimeType, fileName));
  return rank === -1 ? TYPE_ORDER.length : rank;
};

export function fileSortEntry(file: ShareLinkFile): SortableEntry {
  const name = basenameFromPath(file.path) || file.uid;
  return {
    id: file.uid,
    isFolder: false,
    name,
    size: file.size,
    modifiedTime: parseModifiedTime(file.modifiedAt),
    typeRank: typeRank(file.mimeType, name),
  };
}

export function folderSortEntry(
  folder: {
    name: string;
    size: number;
    modifiedAt: Date | null;
  },
  /** The folder's listing key; a file's uid has no folder counterpart. */
  id: string
): SortableEntry {
  return {
    id,
    isFolder: true,
    name: folder.name,
    size: folder.size,
    modifiedTime: folder.modifiedAt?.getTime() ?? 0,
    // Folders are ranked ahead of every file as a kind, so their type never
    // takes part in the comparison.
    typeRank: -1,
  };
}

function parseModifiedTime(modifiedAt: ShareLinkFile["modifiedAt"]): number {
  if (!modifiedAt) return 0;
  const date = modifiedAt instanceof Date ? modifiedAt : new Date(modifiedAt);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
}

/** Compares two strings; the collator's `compare`, handed to the comparators. */
type StringCompare = (a: string, b: string) => number;

/**
 * One collator per locale, built on first use. `localeCompare` with an options
 * object constructs a fresh `Intl.Collator` on *every* comparison, which
 * dominates the sort: measured over 5000 names it is some twenty times slower
 * than one hoisted collator. A share-link is capped at 50 files, but a space's
 * folder listing is not, and its order is recomputed on every direction flip
 * and language change.
 */
const collators = new Map<string, Intl.Collator>();

const collatorFor = (locale: string): StringCompare => {
  let collator = collators.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, {
      sensitivity: "base",
      numeric: true,
    });
    collators.set(locale, collator);
  }
  // `Intl.Collator.prototype.compare` is bound to its collator, so it can be
  // detached and passed around.
  return collator.compare;
};

/**
 * Ascending comparator for a single column; the name breaks ties, and the id
 * breaks those.
 *
 * The collator is case- and accent-insensitive and numeric, so genuinely
 * different names compare equal ("IMG_1" / "IMG_01", "a.txt" / "A.txt",
 * "resume.pdf" / "résumé.pdf"). Without a strict last word those groups would
 * keep whatever order they arrived in and, since the direction is applied as a
 * sign, would not reverse when the user flips it. The id is compared by code
 * unit: it only has to be strict and stable, not meaningful.
 */
function compareEntriesAsc(
  a: SortableEntry,
  b: SortableEntry,
  key: FileSortKey,
  compare: StringCompare
): number {
  const byId = () => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const byName = () => compare(a.name, b.name) || byId();
  switch (key) {
    case "size":
      return a.size - b.size || byName();
    case "modified":
      return a.modifiedTime - b.modifiedTime || byName();
    case "type":
      // Folders are their own kind, ranked before every file; files cluster by
      // the icon their type column shows (see TYPE_ORDER).
      if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
      return a.typeRank - b.typeRank || byName();
    case "name":
    default:
      return byName();
  }
}

/**
 * The files ordered by `sort` (a new array; the input is left alone). For a
 * listing that also contains folders, build {@link SortableEntry} values and
 * sort them with {@link entryComparator} instead.
 */
export const sortFiles = (
  files: ShareLinkFile[],
  sort: FileSort,
  locale: string
): ShareLinkFile[] => {
  // Sort keys are derived once per file rather than on every comparison.
  const entries = new Map(files.map((file) => [file.uid, fileSortEntry(file)]));
  const compare = entryComparator(sort, locale);
  return [...files].sort((a, b) =>
    compare(entries.get(a.uid)!, entries.get(b.uid)!)
  );
};

/**
 * Signed comparator for a mixed folder/file listing under the given sort, with
 * the locale's collator resolved once up front (see {@link collatorFor}).
 * Locale-aware, case- and numeric-insensitive names ("file2" < "file10") break
 * ties. Use with `Array.sort`, which is stable: entries that compare equal keep
 * their original relative order.
 */
export function entryComparator(
  { key, direction }: FileSort,
  locale: string
): (a: SortableEntry, b: SortableEntry) => number {
  const sign = direction === "asc" ? 1 : -1;
  const compare = collatorFor(locale);
  return (a, b) => sign * compareEntriesAsc(a, b, key, compare);
}
